import { DetailedFacility } from "../types/facilityTypes";
import { getApiClient } from "./apiClient";

export interface FacilityCoordinate {
  id: string;
  latitude: number;
  longitude: number;
  facility_type: string[];
  name: string;
}

export interface MapCoordinatesResponse {
  coordinates: FacilityCoordinate[];
  count: number;
}

export interface Organization {
  id: string;
  name: string;
  organization_type?: string[];
  labels: string[];
  description?: string;
  home_country?: string;
  websites?: string[];
  directories?: string[] | null;
  founding_year?: string;
  number_of_employees?: number;
  status_labels?: string[];
  status_summary?: string;
  status_last_update_date?: string;
  services?: string[];
  products?: string[];
  naics_codes_with_title?: string[];
  input_hs_codes_with_title: string[];
  output_hs_codes_with_title: string[];
  financial_kpis?: {
    value: string;
    unit: string;
    metric_name: string;
    reference_business_unit: string;
    reference_period: string;
    summary: string;
  }[];
  production_kpis: {
    capacity_id: string;
    capacity_value: number;
    capacity_unit: string;
    product_or_service: string;
    reference_business_unit: string;
    reference_period: string;
    reference_frequency: string;
    reference_sub_facility_id: string;
    summary: string;
    last_updated_date: string;
  }[];
  confidence_level?: number;
  match_score?: number;
  n_sources?: number;
}

export interface Facility {
  id: string;
  name: string;
  facility_type: string[];
  labels: string[];
  description?: string;
  country_iso2?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  precision_level?: string;
  operational_from?: string;
  number_of_employees?: number;
  services?: string[];
  technologies_used?: string[];
  products_produced?: string[];
  product_use_cases?: string[];
  naics_codes_with_title?: string[];
  input_hs_codes_with_title?: string[];
  output_hs_codes_with_title?: string[];
  production_kpis?: {
    capacity_id: string;
    capacity_value: number;
    capacity_unit: string;
    product_or_service: string;
    reference_business_unit: string;
    reference_period: string;
    reference_frequency: string;
    reference_sub_facility_id: string;
    summary: string;
    last_updated_date: string;
  }[];
  confidence_level?: number;
  match_score: number;
  n_sources?: number;
}

export interface Project {
  id: string;
  name: string;
  project_type: string[];
  description?: string;
  project_status?: string;
  project_start?: string;
  project_end?: string;
  publication_date?: string;
  related_people?: Array<{
    entity_id?: string;
    name: string;
    project_role: string;
    project_role_description?: string;
  }>;
  naics_codes_with_title?: string[];
  project_countries?: string[];
  keywords?: string[];
  project_volume_value?: number;
  project_volume_currency?: string;
  match_score: number;
}

export interface Source {
  id: string;
  title: string;
  text?: string;
  summary?: string;
  url: string;
  domain: string;
  authors?: string[];
  language: string;
  publication_date: string;
  match_score: number;
}

// Graph visualization interfaces
export interface GraphNode {
  id: string;
  name: string;
  type: "organization" | "facility" | "project" | "source";
  val: number;
}

export interface GraphLink {
  id: string;
  source: string;
  target: string;
  type: string;
  label: string;
  strength: number;
}

export interface GraphData {
  nodes: GraphNode[];
  links: GraphLink[];
}

// Edge detail interface for edge detail overlay
export interface EdgeDetail {
  // Source and target entity information
  source_entity: {
    id: string;
    name: string;
    description?: string;
    type: string;
    labels: string;
  };
  target_entity: {
    id: string;
    name: string;
    description?: string;
    type: string;
    labels: string;
  };

  // Relationship metadata
  relationship_labels: string[];
  primary_label: string;
  display_title: string;
  relationship_display: string;

  // Core relationship metadata
  relation_type?: string;
  relation_summary?: string;
  relation_status?: string;

  // Quality metrics
  confidence_score?: number;
  confidence_percentage?: string;
  count?: number;

  // Financial information
  investment_amount?: number;
  investment_currency?: string;
  investment_display?: string;
  ownership_share?: number;
  ownership_percentage?: string;

  // Timeline information
  relation_start?: string;
  relation_end?: string;

  // Context-specific fields
  project_role?: string;
  color?: string;

  // Relation sources
  relation_sources: Array<{
    id: string;
    title: string;
    url: string;
    domain: string;
    publication_date: string;
    authors?: string[];
    summary?: string;
    relation_summary?: string;
  }>;

  // Additional metadata
  id?: string;
}

class GraphService {
  // Maps API - Get facility coordinates for map visualization
  async getMapCoordinates(
    limit: number = 40000,
    filters?: {
      search?: string;
      facility_types?: string[];
      home_country?: string[];
      naics?: string[];
      input_hs_codes?: string[];
      output_hs_codes?: string[];
      facility_owners?: string[];
      facility_owner_max_tiers?: number;
      facility_statuses?: string[];
      suppliers?: string[];
      customers?: string[];
      labels?: string[];
      updated_date_start?: string;
      updated_date_end?: string;
    },
  ): Promise<MapCoordinatesResponse> {
    const params: any = { limit };

    // Add filter parameters if provided
    if (filters) {
      if (filters.search) params.search = filters.search;
      if (filters.facility_types && filters.facility_types.length > 0)
        params.facility_types = filters.facility_types.join(",");
      if (filters.home_country && filters.home_country.length > 0)
        params.home_country = filters.home_country.join(",");
      if (filters.naics && filters.naics.length > 0)
        params.naics = filters.naics.join(",");
      if (filters.input_hs_codes && filters.input_hs_codes.length > 0)
        params.input_hs_codes = filters.input_hs_codes.join(",");
      if (filters.output_hs_codes && filters.output_hs_codes.length > 0)
        params.output_hs_codes = filters.output_hs_codes.join(",");
      if (filters.facility_owners && filters.facility_owners.length > 0)
        params.facility_owners = filters.facility_owners.join(",");
      if (filters.facility_owner_max_tiers)
        params.facility_owner_max_tiers =
          filters.facility_owner_max_tiers.toString();
      if (filters.facility_statuses && filters.facility_statuses.length > 0)
        params.facility_statuses = filters.facility_statuses.join(",");
      if (filters.suppliers && filters.suppliers.length > 0)
        params.suppliers = filters.suppliers.join(",");
      if (filters.customers && filters.customers.length > 0)
        params.customers = filters.customers.join(",");
      if (filters.labels && filters.labels.length > 0)
        params.labels = filters.labels.join(",");
      if (filters.updated_date_start)
        params.updated_date_start = filters.updated_date_start;
      if (filters.updated_date_end)
        params.updated_date_end = filters.updated_date_end;
    }

    const client = await getApiClient();
    const response = await client.get("/graph/map/coordinates/", {
      params,
    });
    return response.data;
  }

  // Facility Details API - Get comprehensive facility information
  async getEntityDetails(entityId: string): Promise<DetailedFacility> {
    try {
      const client = await getApiClient();
      const response = await client.get("/graph/entity/", {
        params: { entity_id: entityId },
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // Organizations API
  async getOrganizations(limit: number = 100): Promise<Organization[]> {
    const client = await getApiClient();
    const response = await client.get("/graph/organizations/", {
      params: { limit },
    });
    return response.data;
  }

  async getOrganization(id: string): Promise<Organization> {
    const client = await getApiClient();
    const response = await client.get(`/graph/organizations/${id}/`);
    return response.data;
  }

  // Projects API
  async getProjects(limit: number = 100): Promise<Project[]> {
    const client = await getApiClient();
    const response = await client.get("/graph/projects/", {
      params: { limit },
    });
    return response.data;
  }

  async getProject(id: string): Promise<Project> {
    const client = await getApiClient();
    const response = await client.get(`/graph/projects/${id}/`);
    return response.data;
  }

  // Sources API
  async getSources(limit: number = 100): Promise<Source[]> {
    const client = await getApiClient();
    const response = await client.get("/graph/sources/", {
      params: { limit },
    });
    return response.data;
  }

  async getSource(id: string): Promise<Source> {
    const client = await getApiClient();
    const response = await client.get(`/graph/sources/${id}/`);
    return response.data;
  }

  // Search and filtering (when implemented)
  async searchEntities(query: string, entityType?: string): Promise<any[]> {
    const client = await getApiClient();
    const response = await client.get("/graph/search/", {
      params: { query, entity_type: entityType },
    });
    return response.data;
  }

  // Graph relationships (when implemented)
  async getEntityRelationships(
    entityId: string,
    entityType: string,
  ): Promise<any[]> {
    const client = await getApiClient();
    const response = await client.get(
      `/graph/relationships/${entityType}/${entityId}/`,
    );
    return response.data;
  }

  // Graph visualization API - Get graph data for visualization
  async getGraphData(
    entityId: string,
    options?: {
      orgStructure?: string;
      supplierTiers?: number;
      customerTiers?: number;
      homeCountry?: string[];
      organizationTypes?: string[];
      naics?: string[];
      inputHsCodes?: string[];
      outputHsCodes?: string[];
      foundingYearMin?: number;
      foundingYearMax?: number;
      labels?: string[];
      relationshipFilters?: any[];
    },
  ): Promise<GraphData> {
    const params: any = { entity: entityId };

    // Add optional parameters
    if (options?.orgStructure) {
      params.org_structure = options.orgStructure;
    }
    if (options?.supplierTiers && options.supplierTiers > 0) {
      params.supplier_tiers = options.supplierTiers;
    }
    if (options?.customerTiers && options.customerTiers > 0) {
      params.customer_tiers = options.customerTiers;
    }

    // Add filter parameters
    if (options?.homeCountry && options.homeCountry.length > 0) {
      params.home_country = options.homeCountry.join(",");
    }
    if (options?.organizationTypes && options.organizationTypes.length > 0) {
      params.organization_types = options.organizationTypes.join(",");
    }
    if (options?.naics && options.naics.length > 0) {
      params.naics = options.naics.join(",");
    }
    if (options?.inputHsCodes && options.inputHsCodes.length > 0) {
      params.input_hs_codes = options.inputHsCodes.join(",");
    }
    if (options?.outputHsCodes && options.outputHsCodes.length > 0) {
      params.output_hs_codes = options.outputHsCodes.join(",");
    }
    if (options?.foundingYearMin !== undefined) {
      params.founding_year_min = options.foundingYearMin;
    }
    if (options?.foundingYearMax !== undefined) {
      params.founding_year_max = options.foundingYearMax;
    }
    if (options?.labels && options.labels.length > 0) {
      params.labels = options.labels.join(",");
    }
    if (
      options?.relationshipFilters &&
      options.relationshipFilters.length > 0
    ) {
      // Transform camelCase to snake_case for backend
      const transformedFilters = options.relationshipFilters.map((filter) => ({
        relationship_types: filter.relationshipTypes,
        related_to_entity: filter.targetCompanies?.map((c: any) => c.id) || [],
        max_relationship_tiers: filter.maxTiers || 1,
      }));
      params.relationship_filters = JSON.stringify(transformedFilters);
    }

    try {
      const client = await getApiClient();
      const response = await client.get("/graph/graph/", {
        params,
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  // Edge Detail API - Get comprehensive edge information by relationship ID
  async getEdgeDetail(relationshipId: string): Promise<EdgeDetail> {
    const params = {
      relationship_id: relationshipId,
    };

    try {
      const client = await getApiClient();
      const response = await client.get("/graph/edge/detail/", {
        params,
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }
}

export const graphService = new GraphService();
